import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { defaultSaveField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/utils';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

type UpdateIssueFieldProps = {
	onError?: (error: ApolloError) => void;
	onSuccess?: () => void;
};

const JWM_ISSUE_FIELD_UPDATE = 'jwmIssueFieldUpdate';
export const useUpdateIssueField = ({ onError, onSuccess }: UpdateIssueFieldProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const updateIssueField = useCallback(
		async <T,>({
			issueKey,
			fieldKey,
			newValue,
		}: {
			issueKey: string;
			fieldKey: string;
			newValue: T;
		}) =>
			defaultSaveField(issueKey, fieldKey, newValue, '')
				.then((response) => {
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						`${JWM_ISSUE_FIELD_UPDATE} succeeded`,
						{
							field: newValue,
						},
					);
					onSuccess && onSuccess();
					return response;
				})
				.catch((error) => {
					fireErrorAnalytics({
						meta: {
							id: JWM_ISSUE_FIELD_UPDATE,
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						attributes: {
							message: `${JWM_ISSUE_FIELD_UPDATE} failed`,
							field: fieldKey.startsWith('customfield_') ? 'category' : fieldKey,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					onError && onError(error);
				}),
		[createAnalyticsEvent, onError, onSuccess],
	);

	return updateIssueField;
};
